import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  category: string;
  imageUrl: string;
  description: string;
  requirements: string[];
  salary?: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
  postedDate: Date;
}

@Injectable({
  providedIn: 'root',
})
export class MockJobService {
  private mockJobs: Job[] = [
    // IT Jobs
    {
      id: 'it-1',
      title: 'Senior Software Engineer',
      company: 'TechVantage Solutions',
      location: 'San Francisco, CA',
      category: 'IT',
      imageUrl: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=800',
      description:
        'We are seeking an experienced Senior Software Engineer to join our dynamic team. You will be responsible for designing and developing scalable web applications using modern technologies.',
      requirements: [
        '5+ years of software development experience',
        'Proficiency in JavaScript, TypeScript, and React',
        'Experience with cloud platforms (AWS, Azure)',
        'Strong problem-solving and communication skills',
      ],
      salary: '$120,000 - $150,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'it-2',
      title: 'DevOps Engineer',
      company: 'CloudSync Technologies',
      location: 'Seattle, WA',
      category: 'IT',
      imageUrl: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800',
      description:
        'Join our infrastructure team to build and maintain our cloud-based systems. You will work with cutting-edge technologies to ensure high availability and scalability.',
      requirements: [
        '3+ years of DevOps experience',
        'Knowledge of Kubernetes, Docker, and CI/CD pipelines',
        'Experience with monitoring and logging tools',
        'Linux system administration skills',
      ],
      salary: '$110,000 - $140,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:30:00'),
    },
    {
      id: 'it-3',
      title: 'Frontend Developer',
      company: 'Digital Dynamics Inc.',
      location: 'Remote',
      category: 'IT',
      imageUrl: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800',
      description:
        'We are looking for a talented Frontend Developer to create beautiful and intuitive user interfaces. You will work closely with designers and backend developers.',
      requirements: [
        '2+ years of frontend development experience',
        'Strong knowledge of HTML, CSS, and JavaScript',
        'Experience with modern frameworks (React, Vue, or Angular)',
        'Portfolio demonstrating UI/UX skills',
      ],
      salary: '$90,000 - $120,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:00:00'),
    },
    // Construction Jobs
    {
      id: 'construction-1',
      title: 'Project Manager',
      company: 'Summit Construction Group',
      location: 'Denver, CO',
      category: 'Construction',
      imageUrl: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
      description:
        'Lead construction projects from planning to completion. Manage teams, budgets, and timelines while ensuring quality and safety standards.',
      requirements: [
        '5+ years of construction project management experience',
        'PMP certification preferred',
        'Strong leadership and communication skills',
        'Knowledge of construction software and tools',
      ],
      salary: '$85,000 - $110,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'construction-2',
      title: 'Construction Superintendent',
      company: 'Metro Builders LLC',
      location: 'Chicago, IL',
      category: 'Construction',
      imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800',
      description:
        'Oversee daily operations on construction sites, coordinate with subcontractors, and ensure projects meet safety and quality standards.',
      requirements: [
        '3+ years of construction supervision experience',
        'OSHA certification required',
        'Ability to read blueprints and construction documents',
        'Strong organizational and problem-solving skills',
      ],
      salary: '$70,000 - $95,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:00:00'),
    },
    {
      id: 'construction-3',
      title: 'Civil Engineer',
      company: 'BuildRight Contractors',
      location: 'Austin, TX',
      category: 'Construction',
      imageUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800',
      description:
        'Design and oversee construction projects including roads, bridges, and buildings. Work with architects and construction teams.',
      requirements: [
        'Bachelor\'s degree in Civil Engineering',
        'Professional Engineer (PE) license preferred',
        'Experience with CAD software',
        'Strong analytical and problem-solving skills',
      ],
      salary: '$75,000 - $100,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:30:00'),
    },
    // Medical Jobs
    {
      id: 'medical-1',
      title: 'Registered Nurse',
      company: 'MedCare Innovations',
      location: 'Boston, MA',
      category: 'Medical',
      imageUrl: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800',
      description:
        'Provide compassionate patient care in our state-of-the-art medical facility. Work with a team of healthcare professionals.',
      requirements: [
        'Active RN license',
        '2+ years of nursing experience',
        'BLS and ACLS certifications',
        'Excellent communication and patient care skills',
      ],
      salary: '$65,000 - $85,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'medical-2',
      title: 'Medical Technologist',
      company: 'HealthFirst Medical Systems',
      location: 'Phoenix, AZ',
      category: 'Medical',
      imageUrl: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800',
      description:
        'Perform laboratory tests and analyze results to help diagnose and treat patients. Work in our modern laboratory facility.',
      requirements: [
        'Bachelor\'s degree in Medical Technology or related field',
        'ASCP certification preferred',
        'Experience with laboratory equipment',
        'Attention to detail and accuracy',
      ],
      salary: '$55,000 - $75,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:00:00'),
    },
    {
      id: 'medical-3',
      title: 'Physical Therapist',
      company: 'Precision Health Partners',
      location: 'Portland, OR',
      category: 'Medical',
      imageUrl: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800',
      description:
        'Help patients recover from injuries and improve mobility. Develop and implement treatment plans in our rehabilitation center.',
      requirements: [
        'Doctor of Physical Therapy (DPT) degree',
        'State licensure',
        '2+ years of clinical experience',
        'Strong interpersonal and communication skills',
      ],
      salary: '$75,000 - $95,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:00:00'),
    },
    // Manufacturing Jobs
    {
      id: 'manufacturing-1',
      title: 'Production Manager',
      company: 'Precision Manufacturing Co.',
      location: 'Detroit, MI',
      category: 'Manufacturing',
      imageUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800',
      description:
        'Oversee manufacturing operations and ensure efficient production processes. Lead teams and optimize workflows.',
      requirements: [
        '5+ years of manufacturing management experience',
        'Knowledge of lean manufacturing principles',
        'Strong leadership and problem-solving skills',
        'Experience with quality control systems',
      ],
      salary: '$80,000 - $105,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'manufacturing-2',
      title: 'Quality Control Inspector',
      company: 'Industrial Manufacturing Group',
      location: 'Cleveland, OH',
      category: 'Manufacturing',
      imageUrl: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800',
      description:
        'Inspect products and materials to ensure they meet quality standards. Document findings and work with production teams.',
      requirements: [
        '2+ years of quality control experience',
        'Attention to detail and accuracy',
        'Knowledge of quality standards and testing methods',
        'Ability to use measurement tools and equipment',
      ],
      salary: '$45,000 - $60,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:00:00'),
    },
    {
      id: 'manufacturing-3',
      title: 'Mechanical Engineer',
      company: 'Advanced Manufacturing Systems',
      location: 'Milwaukee, WI',
      category: 'Manufacturing',
      imageUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800',
      description:
        'Design and improve manufacturing equipment and processes. Work on automation and efficiency projects.',
      requirements: [
        'Bachelor\'s degree in Mechanical Engineering',
        '3+ years of manufacturing engineering experience',
        'Proficiency in CAD software',
        'Knowledge of manufacturing processes',
      ],
      salary: '$75,000 - $100,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:00:00'),
    },
    // Finance Jobs
    {
      id: 'finance-1',
      title: 'Financial Analyst',
      company: 'Global Finance Partners',
      location: 'New York, NY',
      category: 'Finance',
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
      description:
        'Analyze financial data and create reports to support business decisions. Work with cross-functional teams on budgeting and forecasting.',
      requirements: [
        'Bachelor\'s degree in Finance, Accounting, or related field',
        '2+ years of financial analysis experience',
        'Proficiency in Excel and financial modeling',
        'Strong analytical and communication skills',
      ],
      salary: '$65,000 - $85,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'finance-2',
      title: 'Investment Advisor',
      company: 'Capital Wealth Management',
      location: 'Los Angeles, CA',
      category: 'Finance',
      imageUrl: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800',
      description:
        'Help clients achieve their financial goals through personalized investment strategies. Build relationships and provide financial planning services.',
      requirements: [
        'Series 7 and Series 66 licenses',
        '3+ years of financial advisory experience',
        'Strong sales and relationship-building skills',
        'Knowledge of investment products and markets',
      ],
      salary: '$70,000 - $120,000 + commission',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:00:00'),
    },
    {
      id: 'finance-3',
      title: 'Accountant',
      company: 'FinTech Innovations',
      location: 'Atlanta, GA',
      category: 'Finance',
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800',
      description:
        'Prepare financial statements, manage accounts, and ensure compliance with tax regulations. Work in a fast-paced fintech environment.',
      requirements: [
        'Bachelor\'s degree in Accounting',
        'CPA certification preferred',
        '2+ years of accounting experience',
        'Proficiency in accounting software',
      ],
      salary: '$55,000 - $75,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:00:00'),
    },
    // Education Jobs
    {
      id: 'education-1',
      title: 'High School Teacher',
      company: 'Learning Academy Network',
      location: 'Miami, FL',
      category: 'Education',
      imageUrl: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800',
      description:
        'Teach high school students in a supportive and innovative learning environment. Develop engaging lesson plans and support student success.',
      requirements: [
        'Bachelor\'s degree in Education or subject area',
        'State teaching certification',
        '2+ years of teaching experience preferred',
        'Passion for education and student development',
      ],
      salary: '$45,000 - $65,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'education-2',
      title: 'Curriculum Developer',
      company: 'EduTech Solutions',
      location: 'Remote',
      category: 'Education',
      imageUrl: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800',
      description:
        'Design and develop educational content and curricula for online learning platforms. Work with subject matter experts and instructional designers.',
      requirements: [
        'Master\'s degree in Education or related field',
        '3+ years of curriculum development experience',
        'Knowledge of instructional design principles',
        'Strong writing and communication skills',
      ],
      salary: '$60,000 - $80,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:00:00'),
    },
    {
      id: 'education-3',
      title: 'School Counselor',
      company: 'University Online Programs',
      location: 'Minneapolis, MN',
      category: 'Education',
      imageUrl: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800',
      description:
        'Provide academic and career counseling to students. Help students navigate educational choices and plan for their future.',
      requirements: [
        'Master\'s degree in Counseling or School Counseling',
        'State counseling license',
        '2+ years of counseling experience',
        'Strong interpersonal and communication skills',
      ],
      salary: '$50,000 - $70,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:00:00'),
    },
    // Agriculture Jobs
    {
      id: 'agriculture-1',
      title: 'Farm Manager',
      company: 'GreenFields Agriculture',
      location: 'Fresno, CA',
      category: 'Agriculture',
      imageUrl: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800',
      description:
        'Manage daily farm operations including crop production, livestock care, and farm maintenance. Oversee staff and ensure sustainable practices.',
      requirements: [
        '5+ years of farm management experience',
        'Knowledge of sustainable farming practices',
        'Strong leadership and organizational skills',
        'Understanding of agricultural equipment and technology',
      ],
      salary: '$60,000 - $80,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'agriculture-2',
      title: 'Agricultural Scientist',
      company: 'Sustainable Farms Inc.',
      location: 'Ames, IA',
      category: 'Agriculture',
      imageUrl: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800',
      description:
        'Conduct research to improve crop yields and develop sustainable farming methods. Work in our research facility and field locations.',
      requirements: [
        'Master\'s degree in Agricultural Science or related field',
        '2+ years of research experience',
        'Knowledge of plant biology and soil science',
        'Strong analytical and research skills',
      ],
      salary: '$65,000 - $85,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:00:00'),
    },
    {
      id: 'agriculture-3',
      title: 'Agricultural Equipment Technician',
      company: 'Organic Harvest Co-op',
      location: 'Des Moines, IA',
      category: 'Agriculture',
      imageUrl: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=800',
      description:
        'Maintain and repair agricultural equipment including tractors, harvesters, and irrigation systems. Ensure equipment operates efficiently.',
      requirements: [
        '2+ years of equipment maintenance experience',
        'Knowledge of diesel engines and hydraulic systems',
        'Mechanical aptitude and problem-solving skills',
        'Ability to work in outdoor conditions',
      ],
      salary: '$45,000 - $60,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:00:00'),
    },
    // Retail Jobs
    {
      id: 'retail-1',
      title: 'Store Manager',
      company: 'RetailMax Corporation',
      location: 'Dallas, TX',
      category: 'Retail',
      imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
      description:
        'Lead store operations, manage staff, and ensure excellent customer service. Drive sales and maintain store standards.',
      requirements: [
        '3+ years of retail management experience',
        'Strong leadership and customer service skills',
        'Ability to work flexible hours including weekends',
        'Experience with inventory management',
      ],
      salary: '$50,000 - $70,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T10:00:00'),
    },
    {
      id: 'retail-2',
      title: 'Visual Merchandiser',
      company: 'StyleHub Retail',
      location: 'New York, NY',
      category: 'Retail',
      imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
      description:
        'Create attractive store displays and window presentations. Work with the marketing team to showcase products effectively.',
      requirements: [
        '2+ years of visual merchandising experience',
        'Creative eye and design skills',
        'Knowledge of retail trends and customer behavior',
        'Ability to work with design software',
      ],
      salary: '$40,000 - $55,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T09:00:00'),
    },
    {
      id: 'retail-3',
      title: 'E-commerce Specialist',
      company: 'MegaMart Retail Chain',
      location: 'Remote',
      category: 'Retail',
      imageUrl: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
      description:
        'Manage online store operations, product listings, and digital marketing campaigns. Optimize the e-commerce experience for customers.',
      requirements: [
        '2+ years of e-commerce experience',
        'Knowledge of online platforms (Shopify, WooCommerce, etc.)',
        'Understanding of digital marketing and SEO',
        'Strong analytical skills',
      ],
      salary: '$50,000 - $70,000',
      type: 'Full-time',
      postedDate: new Date('2025-01-01T08:00:00'),
    },
  ];

  getJobsByCategory(category: string): Observable<Job[]> {
    const categoryJobs = this.mockJobs.filter(
      (job) => job.category === category
    );
    // Return first 3 jobs for the category
    return of(categoryJobs.slice(0, 3)).pipe(delay(200));
  }

  getJob(id: string): Observable<Job | undefined> {
    const job = this.mockJobs.find((j) => j.id === id);
    return of(job).pipe(delay(200));
  }

  getAllJobs(): Observable<Job[]> {
    return of(this.mockJobs).pipe(delay(300));
  }

  private readonly LIKED_JOBS_KEY = 'liked_jobs';

  getLikedJobs(): string[] {
    const likedJobsJson = localStorage.getItem(this.LIKED_JOBS_KEY);
    if (likedJobsJson) {
      try {
        return JSON.parse(likedJobsJson);
      } catch {
        return [];
      }
    }
    return [];
  }

  likeJob(jobId: string): Observable<boolean> {
    const likedJobs = this.getLikedJobs();
    if (!likedJobs.includes(jobId)) {
      likedJobs.push(jobId);
      localStorage.setItem(this.LIKED_JOBS_KEY, JSON.stringify(likedJobs));
    }
    return of(true).pipe(delay(100));
  }

  unlikeJob(jobId: string): Observable<boolean> {
    const likedJobs = this.getLikedJobs();
    const index = likedJobs.indexOf(jobId);
    if (index > -1) {
      likedJobs.splice(index, 1);
      localStorage.setItem(this.LIKED_JOBS_KEY, JSON.stringify(likedJobs));
    }
    return of(true).pipe(delay(100));
  }

  isJobLiked(jobId: string): boolean {
    return this.getLikedJobs().includes(jobId);
  }

  getLikedJobsWithDetails(): Observable<Job[]> {
    const likedJobIds = this.getLikedJobs();
    const likedJobs = this.mockJobs.filter((job) =>
      likedJobIds.includes(job.id)
    );
    return of(likedJobs).pipe(delay(100));
  }
}

